import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { ActivityDefinition, Bundle, ChargeItemDefinition, CodeSystem } from 'fhir/r4';
import { catchError, firstValueFrom, forkJoin, map, of, switchMap, take } from 'rxjs';
import { backendEndPointToken, serviceRequestCategoriesToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { CustomTabsComponent, TabItem } from '../shared/custom-tabs/custom-tabs.component';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { ErrorService } from '../shared/error.service';
import { StateService } from '../shared/state.service';
import { UtilityService } from '../shared/utility.service';
import { DisableCountParam } from '../loading.interceptor';
import { ServiceDetailsRow, ServiceDetailsService } from './service-details.service';
import { AuthService } from '../shared/auth/auth.service';

@Component({
  selector: 'app-service-details-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTableModule,
    CustomTabsComponent,
    EmptyStateComponent
  ],
  templateUrl: './service-details.component.html',
  styleUrl: './service-details.component.scss'
})
export class ServiceDetailsComponent {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly terminologyCanonicalUrlBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly serviceCategories = inject(serviceRequestCategoriesToken);
  private readonly stateService = inject(StateService);
  private readonly errorService = inject(ErrorService);
  private readonly authService = inject(AuthService);

  displayedColumns: Array<'service' | 'code' | 'category' | 'active' | 'items' | 'price' | 'actions'> = [
    'service',
    'code',
    'category',
    'active',
    'items',
    'price',
    'actions',
  ];

  tabs: TabItem[] = [
    {
      id: 'configured',
      label: 'Configured',
      title: 'Services that have ActivityDefinition (items usage computation) linked to their code',
    },
    {
      id: 'not-configured',
      label: 'Not Configured',
      title: 'Services that do not yet have ActivityDefinition (items usage computation)',
    },
  ];

  activeTabId: TabItem['id'] = 'configured';

  private rowsConfigured: Array<ServiceDetailsRow> = [];
  private rowsNotConfigured: Array<ServiceDetailsRow> = [];
  private utilityService = inject(UtilityService);
  
  searchQuery = '';

  get rows(): Array<ServiceDetailsRow> {
    let currentRows = this.activeTabId === 'configured' ? this.rowsConfigured : this.rowsNotConfigured;
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      currentRows = currentRows.filter(r => 
        (r.display || '').toLowerCase().includes(q) || 
        (r.code || '').toLowerCase().includes(q) ||
        (r.categoryDisplay || '').toLowerCase().includes(q)
      );
    }
    return currentRows;
  }

  onSearchChange(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  ngOnInit(): void {
    this.reload();
  }

  onTabClicked(tabId: string): void {
    this.activeTabId = tabId as any;
  }
  
  canEditRow(row: ServiceDetailsRow): boolean {
    const user = this.authService.user.getValue();
    if (!user) return false;
    
    const role = (user.role || '').toLowerCase();
    if (role === 'superadmin' || role === 'hospitaladmin') return true;

    if ((role === 'lab' || role === 'labtechnician') && (row.categoryDisplay || '').toLowerCase() === 'laboratory') {
      return true;
    }

    if (row.publisher && user.fhirResourceId) {
      const practitionerRef = `Practitioner/${user.fhirResourceId}`;
      if (row.publisher === practitionerRef) {
        return true;
      }
    }

    return false;
  }

  serviceDetailsService = inject(ServiceDetailsService)
  reload(): void {
    this.serviceDetailsService.loadServiceDetails().subscribe({
      next: ({ configured, notConfigured }) => {
        this.rowsConfigured = configured || [];
        this.rowsNotConfigured = notConfigured || [];
      },
      error: (err) => {
        console.error('Failed to load service details list', err);
        this.errorService.openandCloseError('Failed to load service details.');
      },
    });
  }

  reload_old(): void {
    forkJoin({
      codeSystem: this.loadServiceRequestCodeSystem(),
      activities: this.loadActivityDefinitions(),
      charges: this.loadChargeItemDefinitions(),
    })
      .pipe(
        map(({ codeSystem, activities, charges }) => {
          const activeActivities = (activities || []).filter((a) => (a as any)?.status === 'active');
          const activeCharges = (charges || []).filter((c) => (c as any)?.status === 'active');

          const chargeByCode = new Map<string, ChargeItemDefinition>();
          activeCharges.forEach((c) => {
            const code = c?.code?.coding?.[0]?.code;
            if (code) chargeByCode.set(String(code), c);
          });

          const activityByCode = new Map<string, ActivityDefinition>();
          activeActivities.forEach((ad) => {
            const code = ((ad as any)?.code?.coding?.[0]?.code || '').toString().trim();
            if (!code) return;
            if (!this.isItemsUsageComputationActivity(ad)) return;
            if (!activityByCode.has(code)) {
              activityByCode.set(code, ad);
            }
          });

          const allRows: ServiceDetailsRow[] = (codeSystem || []).map((c) => {
            const code = (c.code || '').toString();
            let activity = activityByCode.get(code);
            // if(!activity){
            //   //ry one more time , i need acivity defition whose code matches service request code system 
            //   //try threetierqueryonce
            // const anotherValue =  await firstValueFrom(this.utilityService.threeTierQueryAndSyncToStateOnce$<ActivityDefinition>({
            //     resourceType: 'ActivityDefinition',
            //     context: new HttpContext().set(DisableCountParam, true),
            //     urlSearchObject: {
            //       status: 'active',
            //       topic: 'items usage competition'
            //     },
            //     scope: 'orgWide',
            //     querySearchObject: [
            //       {
            //         key: 'status',
            //         value: 'active'
            //       },
            //       {
            //         key: 'topic.text',
            //         value: 'items usage competition'
            //       },
            //       {
            //         key: 'code.coding.code',
            //         value: `${code}`
            //       }
            //     ]

            //   }))
            //   if(anotherValue[0]){
            //     activity = anotherValue[0]
            //   }



            // }
            const charge = chargeByCode.get(code);
            return {
              code,
              display: c.display || code,
              categoryDisplay: this.getCategoryDisplay(c.categoryCode),
              active: c.active,
              itemsRequiredCount: activity ? this.countItemsRequired(activity) : 0,
              totalPriceDisplay: this.formatSellingPrice(charge),
              hasActivityDefinition: !!activity,
              hasChargeItemDefinition: !!charge,
            };
          });

          return {
            configured: allRows.filter((r) => r.hasActivityDefinition),
            notConfigured: allRows.filter((r) => !r.hasActivityDefinition),
          };
        })
      )
      .subscribe({
        next: ({ configured, notConfigured }) => {
          this.rowsConfigured = configured || [];
          this.rowsNotConfigured = notConfigured || [];
        },
        error: (err) => {
          console.error('Failed to load service details list', err);
          this.errorService.openandCloseError('Failed to load service details.');
        },
      });
  }

  private loadServiceRequestCodeSystem() {
    const canonicalUrl = `${this.terminologyCanonicalUrlBase}/CodeSystem/ServiceRequest/ServiceRequestCodes`;
    const searchUrl = `${this.backendUrl}/CodeSystem?url=${encodeURIComponent(canonicalUrl)}&_count=1`;

    const mapConceptsFromCodeSystem = (cs: CodeSystem | null | undefined): ServiceCodeSystemConcept[] => {
      const concepts: any[] = Array.isArray((cs as any)?.concept) ? ((cs as any).concept as any[]) : [];
      return concepts
        .map((concept) => {
          const code = (concept?.code || '').toString().trim();
          if (!code) return null;

          const display = (concept?.display || '').toString().trim() || code;
          const props: any[] = Array.isArray(concept?.property) ? concept.property : [];
          const categoryProp = props.find((p: any) => p?.code === 'category');
          const activeProp = props.find((p: any) => p?.code === 'active');

          const categoryCode = (categoryProp?.valueString || categoryProp?.valueCode || '').toString().trim() || null;
          const active = activeProp?.valueBoolean !== false;

          return {
            code,
            display,
            categoryCode,
            active,
          } satisfies ServiceCodeSystemConcept;
        })
        .filter(Boolean) as ServiceCodeSystemConcept[];
    };

    return this.stateService.orgWideResources.codeSystems.pipe(
      take(1),
      switchMap((cached) => {
        const cachedResource = (cached || [])
          .map((x) => x.actualResource)
          .find((cs) => ((cs as any)?.url || '').toString() === canonicalUrl);

        const cachedConcepts = mapConceptsFromCodeSystem(cachedResource);
        if (cachedConcepts.length) {
          return of(cachedConcepts);
        }

        return this.http.get<Bundle>(searchUrl).pipe(
          take(1),
          map((bundle: any) => {
            const cs = (bundle?.entry?.[0]?.resource || null) as CodeSystem | null;

            if (cs) {
              const nextList = Array.isArray(cached) ? [...cached] : [];
              const id = (cs as any)?.id ? String((cs as any).id) : null;
              const idx = id
                ? nextList.findIndex((x) => (x?.actualResource as any)?.id && String((x.actualResource as any).id) === id)
                : nextList.findIndex((x) => ((x?.actualResource as any)?.url || '').toString() === canonicalUrl);

              const wrapper = {
                actualResource: cs,
                referenceId: id ? `CodeSystem/${id}` : null,
                savedStatus: 'saved' as const,
              };

              if (idx >= 0) {
                nextList[idx] = wrapper;
              } else {
                nextList.push(wrapper);
              }

              this.stateService.orgWideResources.codeSystems.next(nextList);
            }

            return mapConceptsFromCodeSystem(cs);
          }),
          catchError(() => of([]))
        );
      })
    );
  }

  private getCategoryDisplay(categoryCode: string | null): string {
    if (!categoryCode) return '';
    const match = (this.serviceCategories || []).find((c) => c?.code === categoryCode);
    return match?.display || categoryCode;
  }

  private isItemsUsageComputationActivity(activity: ActivityDefinition): boolean {
    const topics: any[] = Array.isArray((activity as any)?.topic) ? ((activity as any).topic as any[]) : [];
    return topics.some((t: any) => (t?.text || '').toString().trim().toLowerCase() === 'items usage computation');
  }

  private loadChargeItemDefinitions() {
    return this.stateService.orgWideResources.chargeItemDefinitions.pipe(
      take(1),
      switchMap((defs) => {
        if (defs && defs.length) return of(defs.map((d) => d.actualResource));

        return this.http.get<Bundle>(`${this.backendUrl}/ChargeItemDefinition?status=active&_count=500`).pipe(
          take(1),
          map((bundle) => (bundle.entry || []).map((e: any) => e.resource as ChargeItemDefinition)),
          map((resources) => {
            this.stateService.orgWideResources.chargeItemDefinitions.next(
              resources.map((d) => ({
                actualResource: d,
                referenceId: d?.id ? `ChargeItemDefinition/${d.id}` : null,
                savedStatus: 'saved',
              }))
            );
            return resources;
          })
        );
      })
    );
  }

  private loadActivityDefinitions() {
    return this.stateService.orgWideResources.activityDefinitions.pipe(
      take(1),
      switchMap((defs) => {
        if (defs && defs.length) return of(defs.map((d) => d.actualResource));

        return this.http.get<Bundle>(`${this.backendUrl}/ActivityDefinition?status=active&_count=500`).pipe(
          take(1),
          map((bundle) => (bundle.entry || []).map((e: any) => e.resource as ActivityDefinition)),
          map((resources) => {
            this.stateService.orgWideResources.activityDefinitions.next(
              resources.map((d) => ({
                actualResource: d,
                referenceId: d?.id ? `ActivityDefinition/${d.id}` : null,
                savedStatus: 'saved',
              }))
            );
            return resources;
          })
        );
      })
    );
  }

  private countItemsRequired(activity: ActivityDefinition): number {
    const exts = Array.isArray((activity as any)?.extension) ? (activity as any).extension : [];
    return exts.filter((e: any) => e?.url === 'http://hl7.org/fhir/StructureDefinition/items-required').length;
  }

  private formatSellingPrice(charge?: ChargeItemDefinition): string {
    if (!charge) return '';
    const result = this.utilityService.seperateTotalPriceComponents(charge);
    if (!result) return '';

    const currency = result.currency || 'NGN';
    const total = Number(result.totalSellingPrice || 0);

    return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(total);
  }
}

type ServiceCodeSystemConcept = {
  code: string;
  display: string;
  categoryCode: string | null;
  active: boolean;
};

type ServiceDetailsRow2 = {
  code: string;
  display: string;
  categoryDisplay: string;
  active: boolean;
  itemsRequiredCount: number;
  totalPriceDisplay: string;
  hasActivityDefinition: boolean;
  hasChargeItemDefinition: boolean;
};
